import { useCallback, useMemo, useState } from 'react';
import { LabelMenuButton, Menu, MenuContent, type MenuOption, MenuTrigger } from '@optiaxiom/react';

import { useGetAgentsWithPagination } from '@frontend/features/agents/hooks/useGetAgentsWithPagination';
import { useUnifiedAgentByHumanReadableId } from '@frontend/features/agents/hooks/useUnifiedAgentByHumanReadableId';
import { useDebounce, useInstanceId } from '@frontend/hooks';

const ALL_AGENTS_LABEL = 'All Agents';
const AGENT_PAGE_SIZE = 200;
const SEARCH_DEBOUNCE_MS = 300;

const selectedGroup: MenuOption['group'] = {
  label: 'Selected',
  priority: 100,
  hidden: true,
  separator: true,
};

type AgentFilterMenuProps = {
  agentId: string | undefined;
  onChange: (agentId: string | undefined) => void;
};

export function AgentFilterMenu({ agentId, onChange }: AgentFilterMenuProps) {
  const { instanceId } = useInstanceId();
  const [hasOpened, setHasOpened] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebounce(searchInput, SEARCH_DEBOUNCE_MS);

  const { data, isFetching, isError } = useGetAgentsWithPagination({
    instanceId: instanceId ?? '',
    pageSize: AGENT_PAGE_SIZE,
    searchQuery: debouncedSearch,
    enabled: hasOpened,
  });
  const agents = useMemo(() => data?.items ?? [], [data]);

  const { agent: selectedAgent } = useUnifiedAgentByHumanReadableId(agentId ?? '');
  const selectedAgentName = selectedAgent?.name;
  const selectedInResults = !!agentId && agents.some(a => a.id === agentId);

  const isSearching = !!searchInput;

  const options: MenuOption[] = useMemo(() => {
    const result: MenuOption[] = [
      {
        key: 'all-agents',
        label: ALL_AGENTS_LABEL,
        selected: agentId === undefined,
        execute: () => onChange(undefined),
        visible: ({ inputValue }) => !inputValue,
      },
    ];

    if (agentId && !selectedInResults) {
      result.push({
        key: `selected-${agentId}`,
        label: selectedAgentName ?? agentId,
        selected: true,
        execute: () => onChange(undefined),
        group: selectedGroup,
        visible: ({ inputValue }) => !inputValue,
      });
    }

    for (const agent of agents) {
      result.push({
        // Stable React key — without this, agents that share a name produce
        // ghost-duplicates in Axiom Menu's virtualized list.
        key: agent.id,
        label: agent.name,
        selected: agentId === agent.id,
        execute: () => onChange(agent.id),
      });
    }

    if (isError) {
      result.push({
        key: 'error',
        label: 'Failed to load agents',
        intent: 'danger',
        disabledReason: 'Failed to load agents',
        execute: () => {},
      });
    }

    return result;
  }, [agentId, agents, isError, onChange, selectedAgentName, selectedInResults]);

  const handleOpenChange = useCallback((open: boolean) => {
    if (open) {
      setHasOpened(true);
    } else {
      setSearchInput('');
    }
  }, []);

  const isDebouncing = searchInput !== debouncedSearch && !!searchInput;
  const isLoading = isFetching || isDebouncing;

  const triggerLabel =
    agentId === undefined
      ? ALL_AGENTS_LABEL
      : (agents.find(a => a.id === agentId)?.name ?? selectedAgentName ?? agentId);

  return (
    <Menu
      options={options}
      inputVisible="always"
      placeholder="Search agents..."
      onInputValueChange={setSearchInput}
      onOpenChange={handleOpenChange}
      loading={isLoading ? 'spinner' : undefined}
      empty={isSearching ? 'No agents found.' : 'No agents available.'}
    >
      <MenuTrigger asChild>
        <LabelMenuButton label="Agent">{triggerLabel}</LabelMenuButton>
      </MenuTrigger>
      <MenuContent />
    </Menu>
  );
}
